import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface FiltersCardProps {
  onSearch: () => void;
  /** "Clear filters" handler. Prop name kept as `onCancel` for back-compat
   * with the original MUI version's API; the button label uses
   * `common.clear` ("Clear" / "Limpar" / "Effacer"). */
  onCancel: () => void;
  children?: React.ReactNode;
}

/*
 * Shadcn re-implementation of the original MUI filters card. Same API
 * (onSearch / onCancel / children) so the remaining un-migrated consumers
 * (items, flows) keep working without code changes — they still hand in
 * MUI Grid + AutocompleteInput as children. Those still render fine
 * because the MUI ThemeProvider adapter is still mounted; this wrapper
 * just gives the page-level chrome a shadcn Card surface and consistent
 * Button styling.
 *
 * Title key (`inspections.filter.title`) preserved verbatim; rewrites
 * to a more sensible namespace are out of scope for this primitive
 * migration.
 */
export default function FiltersCard({
  onSearch,
  onCancel,
  children,
}: FiltersCardProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="space-y-5">
        <h3 className="text-base font-semibold">
          {t("inspections.filter.title")}
        </h3>
        {children}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onCancel()}>
            {t("common.clear", "Clear")}
          </Button>
          <Button onClick={() => onSearch()}>
            {t("components.table.toolbar.search")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
